/**
 * Best-so-far "climb" split shared by every eval chart: the step line
 * tracks the running best score; points that fail to beat it render as
 * smaller muted dots.
 */

import type { ScoreFormat } from '@/components/evals/format'
import type { ScorePoint } from '@/api/generated/model'

export interface ChartPoint {
  /** X position. Real timestamp until `ordinalize`, then an even index. */
  x: number
  /** Real run timestamp (ms) — kept for tooltips after ordinalize. */
  date: number
  score: number
  model: string
  /** Series label on multi-variant charts; absent on single-series charts. */
  variant?: string
}

export interface ClimbSplit {
  /** Points that beat (or tie) every earlier score — the climb line. */
  best: ChartPoint[]
  /** Points below the best-so-far — muted background dots. */
  below: ChartPoint[]
}

/**
 * The climb LINE's data: a synthetic origin on the axis floor one slot
 * before the first run (so every chart reads "up and to the right"), the
 * best points, extended flat to the last run date when later runs never
 * beat the best — the line always spans the full time range. Rendered as a
 * dotless series so the synthetic ends draw no misleading hoverable dots.
 *
 * `floor` must match the Y domain's min (`scoreFloor`): the origin is real
 * plotted data, so a below-domain one would drag the axis back down to it.
 */
export function climbLineData(best: ChartPoint[], lastX: number, floor = 0): ChartPoint[] {
  const firstBest = best[0]
  const lastBest = best[best.length - 1]
  if (!firstBest || !lastBest) throw new Error('climbLineData called with no best points')
  const line = [{ ...firstBest, x: firstBest.x - 1, score: floor }, ...best]
  if (lastBest.x >= lastX) return line
  return [...line, { ...lastBest, x: lastX }]
}

/** ScorePoints → ChartPoints (scores scaled to the [0,100] axis the climb uses). */
export function toChartData(points: ScorePoint[], variant?: string): ChartPoint[] {
  return points.map((point) => {
    const date = new Date(point.date).getTime()
    return {
      x: date,
      date,
      score: point.score * 100,
      model: point.model,
      variant,
    }
  })
}

/**
 * Axis-tick label for a scaled [0,100] score under the group's format:
 * "73%" (percent) or "0.73" (decimal, for C-index / AUC).
 */
export function axisLabel(value: number, format: ScoreFormat): string {
  return format === 'decimal' ? (value / 100).toFixed(2) : `${value.toString()}%`
}

/** Split chronological points into the best-so-far climb vs. the rest. */
export function splitClimb(data: ChartPoint[]): ClimbSplit {
  const best: ChartPoint[] = []
  const below: ChartPoint[] = []
  let bestScore = -Infinity
  for (const point of data) {
    if (point.score >= bestScore) {
      bestScore = point.score
      best.push(point)
    } else {
      below.push(point)
    }
  }
  return { best, below }
}

/**
 * Full scale for a rate, on the [0,100] axis the climb uses — the ceiling a
 * percent or [0,1] metric can never pass.
 */
export const FULL_SCALE = 100

/**
 * Padded Y max for score charts (~5pt breathing room).
 *
 * A rate stops at `FULL_SCALE`: padding a percent axis past 100% invents
 * headroom the metric does not have. An unbounded metric (SNR, a ratio) has
 * no such ceiling, so once the data clears full scale the padding turns
 * proportional — a flat +5 on a max of 1330 is a sliver, and the top point
 * would sit flush against the frame.
 */
export function scoreDomain(dataMax: number): number {
  if (dataMax > FULL_SCALE) return Math.ceil(dataMax * 1.05)
  return Math.min(FULL_SCALE, Math.ceil(dataMax + 5))
}

/** The reference label the API folds `eval_groups.baseline` into. */
export const RANDOM_LABEL = 'random'

/**
 * Y-axis min: 0, or a FLOOR-LIKE reference level when one exists — the
 * band below chance or below the base model is dead ink on a scored chart,
 * so cutting it stretches the range a model can actually move in.
 *
 * A reference is floor-like when it is the "random" chance level, or when
 * it sits at-or-below every run (a base model's level — the climb grows
 * FROM it). A reference above any run is a target (SOTA, a competitor) and
 * never lifts the floor: zooming the axis to the worst run would turn two
 * numbers into a dramatic window. Without a floor-like reference the floor
 * stays 0: the climb reads "up and to the right" off a true zero.
 *
 * Never rises above something that has to stay visible — a reference BELOW
 * chance (recharts DISCARDS an overflowing `ReferenceLine`) or a
 * below-chance run (recharts silently widens the domain back down to the
 * raw score, leaving a ragged `12.699999` bottom tick). Floored to an
 * integer for the same reason `scoreDomain` ceils: recharts pins the first
 * tick to the domain endpoint and `axisLabel` prints it verbatim.
 */
export function scoreFloor(
  references: { label: string; y: number }[],
  data: ChartPoint[]
): number {
  // Runless charts (references pushed before the first scored run) keep the
  // zero floor: every bar is a reference there, and flooring at the lowest
  // one collapses it to a sliver.
  if (references.length === 0 || data.length === 0) return 0
  const lowestReference = Math.min(...references.map((line) => line.y))
  const lowestRun = Math.min(...data.map((point) => point.score))
  const hasRandom = references.some((line) => line.label === RANDOM_LABEL)
  if (!hasRandom && lowestReference > lowestRun) return 0
  return Math.max(0, Math.floor(Math.min(lowestReference, lowestRun)))
}

/**
 * `scoreDomain` that also clears the chart's reference lines — recharts only
 * measures plotted data, so a SOTA line above every run would sit outside
 * the axis and never render. Scores are on the [0,100] axis scale.
 */
/**
 * The leader of a bar-mode chart, and its margin over second place
 * (TRA-993). The acceptance bar for a trained model is the best OTHER
 * model on the same benchmark, not 100%, so every trailing bar draws its
 * own distance to the leader. When the trained model overtakes, the same
 * rule puts a sketch on the model it just passed.
 */
export interface GapToBest {
  /** Tallest bar's label. Every other bar sketches its gap up to this level. */
  bestLabel: string
  bestScore: number
  /** Second-tallest bar's label — the leader's tooltip names it. */
  runnerUpLabel: string
  /** bestScore - runnerUpScore, on the [0,100] axis. 0 on a tie for first. */
  gap: number
}

/** Null when there is nothing to compare (fewer than two bars). */
export function gapToBest(bars: { label: string; score: number }[]): GapToBest | null {
  const sorted = [...bars].sort((a, b) => b.score - a.score)
  const best = sorted[0]
  const runnerUp = sorted[1]
  if (best === undefined || runnerUp === undefined) return null
  return {
    bestLabel: best.label,
    bestScore: best.score,
    runnerUpLabel: runnerUp.label,
    gap: best.score - runnerUp.score,
  }
}

export function scoreDomainWith(referenceScores: number[]): (dataMax: number) => number {
  return (dataMax: number) => scoreDomain(Math.max(dataMax, ...referenceScores))
}

/**
 * Squash the time axis: map each distinct timestamp (across all series)
 * to an evenly spaced ordinal index, so long idle gaps between runs
 * don't dominate the chart. Returns the remapped series plus the sorted
 * timestamps for tick labeling (index → timestamps[index]).
 */
export function ordinalize(series: ChartPoint[][]): {
  series: ChartPoint[][]
  timestamps: number[]
} {
  const timestamps = Array.from(new Set(series.flat().map((p) => p.date))).sort(
    (a, b) => a - b
  )
  const indexOf = new Map(timestamps.map((t, i) => [t, i]))
  return {
    series: series.map((points) =>
      points.map((point) => {
        const index = indexOf.get(point.date)
        if (index === undefined) {
          throw new Error(`ordinalize: timestamp ${point.date.toString()} missing from index`)
        }
        return { ...point, x: index }
      })
    ),
    timestamps,
  }
}

export interface Caption {
  label: string
  /** Pixel Y of the caption's text baseline. */
  y: number
}

/**
 * Vertical layout for the reference-line captions: nudge baselines apart so
 * two references at near-identical scores don't print on top of each other.
 * Score order is preserved (a caption never jumps past its neighbour), and
 * the spread stack is squeezed back under `max` afterwards so the lowest
 * caption can't be pushed off the plot. Given more captions than the range
 * can hold `gap` apart, the overflow goes off the TOP — the bottom clamp
 * runs last — which lands in the chart's margin instead of under the axis.
 */
export function spreadCaptions(
  captions: Caption[],
  gap: number,
  min: number,
  max: number
): Caption[] {
  const order = captions
    .map((caption, index) => ({ label: caption.label, y: caption.y, index }))
    .sort((a, b) => a.y - b.y)
  let cursor = min
  for (const entry of order) {
    entry.y = Math.max(entry.y, cursor)
    cursor = entry.y + gap
  }
  cursor = max
  for (const entry of [...order].reverse()) {
    entry.y = Math.min(entry.y, cursor)
    cursor = entry.y - gap
  }
  const placed = [...captions]
  for (const entry of order) {
    placed[entry.index] = { label: entry.label, y: entry.y }
  }
  return placed
}

/**
 * The day a run date prints as on the chart's time axis ("Sep 7"), in the
 * viewer's own time zone. Anything that names the same run beside the
 * chart (the "Latest nightly run" box) formats with this, so both name one day:
 * a night's key is midnight UTC, which a Pacific viewer sees as the
 * evening before.
 */
export function formatAxisDay(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Axis config for ordinalized time: one tick per run slot plus a
 * trailing "day after the last run" slot so the newest points don't sit
 * on the chart's right edge. Day labels dedupe (first slot of each day
 * only), so same-day runs don't stutter "Jul 23  Jul 23  Jul 23".
 */
export function ordinalAxis(timestamps: number[]): {
  domain: [number, number]
  ticks: number[]
  format: (index: number) => string
} {
  const labels = timestamps.map(formatAxisDay)
  const last = timestamps[timestamps.length - 1]
  if (last === undefined) throw new Error('ordinalAxis called with no timestamps')
  labels.push(formatAxisDay(last + 24 * 60 * 60 * 1000))
  return {
    // Starts at -1: the slot holding the climb line's synthetic zero origin.
    domain: [-1, timestamps.length],
    ticks: labels.map((_, index) => index),
    format: (index: number) => {
      const label = labels[index]
      if (label === undefined) return ''
      return index > 0 && labels[index - 1] === label ? '' : label
    },
  }
}
